/**
 * @file
 * Sticky header functionality - scales logo on scroll
 */

export function initStickyHeader() {
  const header = document.querySelector('.navbar');
  const logo = document.querySelector('.navbar-brand img');
  if (!header || !logo) return;

  let isSticky = false;
  let ticking = false;

  // Logo sizing parameters
  const LOGO_START_SIZE = 300;  // Initial logo size in pixels
  const LOGO_MIN_SIZE = 120;    // Minimum logo size in pixels
  const SCROLL_START = 88;      // Start resizing after scrolling this many pixels
  const SCROLL_END = 200;       // Stop resizing after this scroll position
  const SCROLL_RANGE = SCROLL_END - SCROLL_START;

  // Hysteresis: Different thresholds for activation and deactivation
  const ACTIVATE_THRESHOLD = 150;   // Activate when scrolling down past this point
  const DEACTIVATE_THRESHOLD = 1;   // Deactivate when scrolling up past this point

  // Set initial state
  if (window.scrollY > ACTIVATE_THRESHOLD) {
    header.classList.add('sticky');
    isSticky = true;
  }

  let lastScrollY = window.scrollY;
  let isScrolling;

  const updateHeader = () => {
    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > lastScrollY;
    lastScrollY = currentScrollY;

    // Only update if user is actively scrolling
    if (isScrolling !== false) {
      // Update logo size based on scroll position
      if (currentScrollY > SCROLL_START) {
        // Calculate new size (decreasing from LOGO_START_SIZE to LOGO_MIN_SIZE)
        const scrollProgress = Math.min(Math.max(currentScrollY - SCROLL_START, 0), SCROLL_RANGE) / SCROLL_RANGE;
        const newSize = Math.round(Math.max(LOGO_MIN_SIZE, LOGO_START_SIZE * (1 - scrollProgress)));
        logo.style.width = `${newSize}px`;
        logo.style.height = 'auto';
      } else if (currentScrollY <= DEACTIVATE_THRESHOLD) {
        // Only reset to original size when scrolling back to top
        logo.style.width = `${LOGO_START_SIZE}px`;
        logo.style.height = 'auto';
      }
    }

    // Reset scrolling state
    clearTimeout(isScrolling);
    isScrolling = setTimeout(() => {
      isScrolling = false;
    }, 66); // ~15fps


    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateHeader);
    }
  };

  // Initial update
  updateHeader();

  // Only add scroll listener on desktop
  const mediaQuery = window.matchMedia('(min-width: 992px)');

  const handleMediaChange = (e) => {
    if (e.matches) {
      // Desktop: add scroll listener and update header
      window.addEventListener('scroll', onScroll, {passive: true});
      updateHeader();
    } else {
      // Mobile: remove scroll listener and reset styles
      window.removeEventListener('scroll', onScroll);
      logo.style.width = '';
      logo.style.height = '';
      logo.style.transition = '';
      header.classList.remove('sticky');
    }
  };

  // Initial setup
  handleMediaChange(mediaQuery);
  mediaQuery.addListener(handleMediaChange);

  // Cleanup function
  return () => {
    mediaQuery.removeListener(handleMediaChange);
    window.removeEventListener('scroll', onScroll);
  };
}
